import { createContext, useContext, useEffect, useState } from 'react'
import { createClient } from '@/lib/supabase'

const AuthContext = createContext({})

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null)
  const [loading, setLoading] = useState(true)
  const [accessLevel, setAccessLevel] = useState(null)
  const [accessLoading, setAccessLoading] = useState(false)
  const supabase = createClient()

  useEffect(() => {
    // Get initial session
    const init = async () => {
      try {
        const {
          data: { session },
          error
        } = await supabase.auth.getSession()
        if (error) console.log('Error fetching session:', error.message)
        setUser(session?.user ?? null)
      } catch (err) {
        console.log('Session fetch error:', err)
      } finally {
        setLoading(false)
      }
    }

    init()

    // Listen for auth changes
    const {
      data: { subscription }
    } = supabase.auth.onAuthStateChange((_event, session) => {
      setUser(session?.user ?? null)
    })

    return () => {
      subscription?.unsubscribe()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useEffect(() => {
    let cancelled = false

    if (!user) {
      setAccessLevel(null)
      setAccessLoading(false)
      return
    }

    const fetchAccessLevel = async () => {
      setAccessLoading(true)
      try {
        const { data, error } = await supabase
          .from('user_access')
          .select('level')
          .eq('id', user.id)
          .single()

        if (cancelled) return

        if (error) {
          console.log('Error fetching access level:', error.message)
          setAccessLevel(null)
          return
        }

        setAccessLevel(data?.level ?? null)
      } catch (err) {
        if (!cancelled) {
          console.log('Error fetching access level:', err)
          setAccessLevel(null)
        }
      } finally {
        if (!cancelled) {
          setAccessLoading(false)
        }
      }
    }

    fetchAccessLevel()

    return () => {
      cancelled = true
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.id])

  return (
    <AuthContext.Provider value={{ user, loading, accessLevel, accessLoading }}>
      {!loading && children}
    </AuthContext.Provider>
  )
}

export const useAuth = () => useContext(AuthContext)
